import React, { useEffect, useId, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import {
  HELP_ISSUE_OTHER_ID,
  buildOrderHelpWhatsAppUrl,
  getOrderHelpIssueOptions,
  getOrderHelpStatusLabel,
  openWhatsAppSupport,
} from './orderHelpUtils';
import { formatOrderAmountInr, formatOrderReferenceDateLine } from './accountOrdersUtils';

/**
 * @param {{
 *   open: boolean;
 *   order: {
 *     displayId: string;
 *     status: string;
 *     total: number | null;
 *     placedAt: Date | null;
 *     raw: Record<string, unknown>;
 *   } | null;
 *   onClose: () => void;
 * }} props
 */
export default function OrderHelpDialog({ open, order, onClose }) {
  const titleId = useId();
  const [issueId, setIssueId] = useState('');
  const [otherDetail, setOtherDetail] = useState('');

  const issueOptions = order ? getOrderHelpIssueOptions(order) : [];
  const showOtherField = issueId === HELP_ISSUE_OTHER_ID;
  const otherValid = !showOtherField || String(otherDetail).trim().length > 0;
  const canContinue = Boolean(issueId && otherValid);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setIssueId('');
      setOtherDetail('');
    }
  }, [open]);

  if (!open || !order) return null;

  const metaLine = formatOrderReferenceDateLine(order.displayId, order.placedAt);
  const statusLabel = getOrderHelpStatusLabel(order);
  const totalLabel = formatOrderAmountInr(order.total);

  const handleContinue = () => {
    if (!canContinue) return;
    const url = buildOrderHelpWhatsAppUrl(order, issueId, otherDetail);
    openWhatsAppSupport(url);
    onClose();
  };

  return (
    <div className="account-orders-help-dialog" role="presentation">
      <button type="button" className="account-orders-help-dialog__backdrop" aria-label="Close" onClick={onClose} />
      <div
        className="account-orders-help-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="account-orders-help-dialog__title">
          Get help with this order
        </h2>

        <div className="account-orders-help-dialog__summary">
          <p className="account-orders-help-dialog__summary-line">{metaLine}</p>
          <p className="account-orders-help-dialog__summary-line">
            <span className="account-orders-help-dialog__summary-label">Status:</span> {statusLabel}
          </p>
          {totalLabel ? (
            <p className="account-orders-help-dialog__summary-line">
              <span className="account-orders-help-dialog__summary-label">Total:</span> {totalLabel}
            </p>
          ) : null}
        </div>

        <fieldset className="account-orders-help-dialog__issues">
          <legend className="account-orders-help-dialog__issues-legend">What do you need help with?</legend>
          <div className="account-orders-help-dialog__chips">
            {issueOptions.map((opt) => {
              const selected = issueId === opt.id;
              return (
                <label
                  key={opt.id}
                  className={`account-orders-help-dialog__chip${selected ? ' account-orders-help-dialog__chip--selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="order-help-issue"
                    className="account-orders-help-dialog__chip-input"
                    value={opt.id}
                    checked={selected}
                    onChange={() => setIssueId(opt.id)}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </fieldset>

        {showOtherField ? (
          <label className="account-orders-help-dialog__other">
            <span className="account-orders-help-dialog__other-label">Tell us briefly</span>
            <textarea
              className="account-orders-help-dialog__other-input"
              rows={3}
              maxLength={200}
              value={otherDetail}
              onChange={(e) => setOtherDetail(e.target.value)}
              placeholder="Describe your issue"
            />
          </label>
        ) : null}

        <div className="account-orders-help-dialog__actions">
          <button
            type="button"
            className="account-orders-help-dialog__btn account-orders-help-dialog__btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="account-orders-help-dialog__btn account-orders-help-dialog__btn--primary"
            disabled={!canContinue}
            onClick={handleContinue}
          >
            <FaWhatsapp aria-hidden />
            Continue on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}
